import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useDatabasesNames } from "services/databases/useDatabasesNames";

import { useMenu } from "components/ActionMenu/useMenu";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import { ROLES_WIZARD_ADD_GROUP_EMPTY } from "../privilegesReducer";
import { schemaPrivileges } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { formatPrivilege } from "../utils";
import { getRenderDatabaseItem } from "./DatabaseItems";
import { GrantPrivilegesSelector } from "./GrantPrivilegesSelector";
import { GroupInner } from "./GroupInner";
import { SchemasItems } from "./SchemasItems";
import { usePrivilegesHandlers } from "./usePrivilegesHandlers";

import styles from "./styles.module.scss";

type Props = {
  index: number;
  resource: string;
};

export const SchemasGroup = (props: Props) => {
  const { index, resource } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();

  const databases = useDatabasesNames();
  const groupState = privilegesState.schema[index];
  const { objects, expanded } = groupState;
  const menu = useMenu();
  const popoverRef = useRef<HTMLDivElement>(null);

  const { onGrantPrivileges, deleteGroup, handleMore } = usePrivilegesHandlers({
    index,
    resource,
  });

  const { t } = useTranslation();

  const menuItems = [
    [
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [],
          });
        },
        testId: "roles-wizard-add-schemas-group",
      },
    ],
    ...(privilegesState.schema.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-schema-group",
            },
          ],
        ]
      : []),
  ];

  const renderItem = getRenderDatabaseItem({
    databases,
    popoverRef,
    renderChildren: (catalogName: string) => (
      <SchemasItems
        catalogName={catalogName}
        index={index}
        resource={resource}
      />
    ),
  });

  const privilegesOptions = schemaPrivileges.map(privilege => {
    return {
      value: privilege,
      text: formatPrivilege(privilege),
    };
  });

  return (
    <div className={styles.group}>
      <GroupInner
        handleMore={handleMore}
        expanded={expanded}
        objects={objects}
        items={databases}
        renderItem={renderItem}
        resource={resource}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_database")}
        searchKey="catalogName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
